import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@data/auth/auth.service';
import { BehaviorSubject, Observable, catchError, debounceTime, filter, finalize, switchMap, tap, throwError } from 'rxjs';

@Injectable()
export class HttpsInterceptor implements HttpInterceptor {

  private AUTH_HEADER = 'Authorization';
  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

	constructor(
    private _authService: AuthService,
  ) {
	}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (request.body instanceof FormData) {
      let ignore =
        typeof request.body === 'undefined' ||
        request.body === null ||
        request.body.toString() === '[object FormData]' || // <-- form data handling
        request.headers.has('Content-Type');

      if (ignore) {
        return next.handle(request);
      }
    }
    if (!request.headers.has('Content-Type')) {
      request = request.clone({
        headers: request.headers.set('Content-Type', 'application/json;charset=utf-8'),
      });
    }

    request = this._addAuthenticationToken(request);

    return next.handle(request).pipe(
      tap((res: any) => {
        if (res instanceof HttpResponse) {
          let hash = res.headers.get('x-lapro-acl');
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          console.error('access denied');
          this._authService.killCookie();

        } else if (error.status === 419) {
          if (this.refreshTokenInProgress) {
            // If refreshTokenInProgress is true, we will wait until refreshTokenSubject has a non-null value
            // which means the new token is ready and we can retry the requests again
            return this.refreshTokenSubject.pipe(
              filter((result) => result !== null),
              debounceTime(0),
              switchMap(() => next.handle(this._addAuthenticationToken(request)))
            );

          } else {
            this.refreshTokenInProgress = true;
            // Set the refreshTokenSubject to null so that subsequent API calls will wait until the new token has been retrieved
            this.refreshTokenSubject.next(null);

            return this._refreshAccessToken().pipe(
              switchMap((data) => {
                this.refreshTokenSubject.next(data);
                this._authService.setToken(data);
                return next.handle(this._addAuthenticationToken(request));
              }),
              // When the call to refreshToken completes we reset the refreshTokenInProgress to false
              // for the next time the token needs to be refreshed
              finalize(() => (this.refreshTokenInProgress = false))
            );
          }
        } else {
          return throwError(() => error);
        }
        return throwError(() => error);
      })
    );
  }

  private _refreshAccessToken(): Observable<any> {
    return this._authService.requestRefresh(this._authService.getToken().refresh_token);
  }

  private _addAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {
    // If we do not have a token yet then we should not set the header.
    // Here we could first retrieve the token from where we store it.

    if (!this._authService.getToken()?.access_token) {
      return request;
    }

    return request.clone({
      headers: request.headers.set(this.AUTH_HEADER, 'Bearer ' + this._authService.getToken().access_token),
      ...(request.method == "GET" || request.method == "POST") && {
        params: (request.params ? request.params : new HttpParams())
        .set('noCache', new Date().getTime() + this._makeid())
      }
    });
  }

  private _makeid() {
    let text = "";
    let possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

    for (let i = 0; i < 5; i++)
      text += possible.charAt(Math.floor(Math.random() * possible.length));

    return text;
  }

}
